/* =============================================================================
 * /pages/sterilization-self-check — Dental sample (Ontario)
 * Front-end controller. Pure browser code, IIFE, no build step.
 * The ONLY external dependency is Tesseract.js, loaded from a CDN in the page.
 *
 * 🔴 Red lines honoured here:
 *   R1  No compliance verdict of any kind. The tool reports only completion
 *       ("You have answered X of 14 items") and three neutral tallies. There is no overall
 *       rating, grade, or verdict word anywhere in this file.
 *   R2  Photos never leave the device. This file performs no outbound data
 *       call of any kind: no request helper, no async transport object, no
 *       beacon call, no multipart form object. Images are read with FileReader
 *       and recognised in-page by Tesseract.js.
 *   R3  Recognition only proposes candidate fields. Nothing is written until
 *       the user confirms it in the confirm form; on failure we ask the user
 *       to type it in.
 * ===========================================================================*/
(function () {
  'use strict';

  var TOTAL = 14;
  var STATE = { INPLACE: 'inplace', VERIFY: 'verify', ACTION: 'action' };

  /* The 🟡 disclaimer that must follow item F1 everywhere it appears. */
  var F1_LABEL = '\uD83D\uDFE1 Not yet verified word-for-word against RCDSO';

  /* Neutral result tokens we may SURFACE AS A CANDIDATE for the user to
     confirm. We deliberately do not decide anything from them. */
  var RESULT_TOKENS = ['failed', 'fail', 'negative', 'positive', 'released', 'hold'];

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function pad2(n) { n = String(n); return n.length < 2 ? '0' + n : n; }

  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ---------------------------------------------------------------- state */

  var app = $('#sc-app');
  var items = $$('[data-sc-item]');
  var progressCount = $('#sc-progress-count');
  var progressFill = $('#sc-progress-fill');
  var progressBar = $('.sc-progress__bar');

  function readState(item) {
    var checked = item.querySelector('input[type="radio"]:checked');
    return checked ? checked.getAttribute('data-state') : null;
  }

  function recompute() {
    var answered = 0, nAction = 0, nVerify = 0, nInplace = 0;

    items.forEach(function (item) {
      var s = readState(item);
      item.setAttribute('data-sc-state', s || 'unanswered');

      /* Action card appears only for a missing / unverified item. */
      var card = item.querySelector('[data-sc-actioncard]');
      if (card) { card.hidden = (s === STATE.INPLACE || !s); }

      /* A2's cadence warning shows only when Weekly / Monthly is chosen. */
      var cadenceMsg = item.querySelector('[data-sc-cadence-risk-msg]');
      if (cadenceMsg) {
        var picked = item.querySelector('input[type="radio"]:checked');
        cadenceMsg.hidden = !(picked && picked.getAttribute('data-sc-cadence-risk') === '1');
      }

      if (s === STATE.INPLACE) { answered++; nInplace++; }
      else if (s === STATE.VERIFY) { answered++; nVerify++; }
      else if (s === STATE.ACTION) { answered++; nAction++; }
    });

    var pct = Math.round((answered / TOTAL) * 100);

    if (progressCount) { progressCount.textContent = answered + ' / ' + TOTAL + ' completed'; }
    if (progressFill) { progressFill.style.width = pct + '%'; }
    if (progressBar) { progressBar.setAttribute('aria-valuenow', String(answered)); }

    $$('[data-sc-answered]').forEach(function (el) { el.textContent = String(answered); });
    var ca = $('[data-sc-count="action"]');  if (ca) ca.textContent = String(nAction);
    var cv = $('[data-sc-count="verify"]');  if (cv) cv.textContent = String(nVerify);
    var ci = $('[data-sc-count="inplace"]'); if (ci) ci.textContent = String(nInplace);
  }

  function onAnyChange(e) {
    if (e.target && e.target.matches('input[type="radio"]')) { recompute(); }
  }
  if (app) { app.addEventListener('change', onAnyChange); }

  /* ------------------------------------------------------ photo + on-device text reading */

  var shotInput = $('#sc-shot-input');
  var drop = $('#sc-drop');
  var thumbs = $('#sc-thumbs');
  var status = $('#sc-ocr-status');
  var confirmForm = $('#sc-confirm');
  var savedList = $('#sc-saved');
  var pendingImage = null;

  function setStatus(msg, isError) {
    if (!status) { return; }
    status.textContent = msg || '';
    status.className = 'sc-ocr-status' + (isError ? ' is-error' : '');
  }

  function addThumb(dataUrl, onRemove) {
    if (!thumbs) { return; }
    var box = document.createElement('div');
    box.className = 'sc-thumb';
    var img = document.createElement('img');
    img.alt = 'Your record (preview, stays on this device)';
    img.src = dataUrl;
    var rm = document.createElement('button');
    rm.type = 'button';
    rm.setAttribute('aria-label', 'Remove this photo');
    rm.textContent = '\u00D7';
    rm.addEventListener('click', function () { if (onRemove) { onRemove(); } box.remove(); });
    box.appendChild(img);
    box.appendChild(rm);
    thumbs.appendChild(box);
  }

  function clearPreview() {
    if (thumbs) { thumbs.innerHTML = ''; }
  }

  function handleFile(file) {
    if (!file) { return; }
    if (!/^image\//.test(file.type || '')) {
      setStatus('That file is not an image — please choose a photo.', true);
      return;
    }

    clearPreview();
    pendingImage = file;

    /* Read locally with FileReader. Nothing is sent anywhere. */
    var reader = new FileReader();
    reader.onload = function (ev) {
      var dataUrl = ev.target.result;
      addThumb(dataUrl, function () { pendingImage = null; });
      runOnDeviceRead(dataUrl);
    };
    reader.onerror = function () {
      showManualFallback('We could not open this photo on your device — please type it in.');
    };
    reader.readAsDataURL(file);
  }

  function runOnDeviceRead(dataUrl) {
    confirmForm.hidden = true;

    if (typeof Tesseract === 'undefined' || !Tesseract || typeof Tesseract.recognize !== 'function') {
      showManualFallback('The on-device reader could not start — please type it in.');
      return;
    }

    setStatus('Reading the text inside your browser… (this stays on your device)');

    Tesseract.recognize(dataUrl, 'eng')
      .then(function (res) {
        var text = (res && res.data && res.data.text) ? res.data.text : '';
        if (!text || !text.trim()) {
          showManualFallback('We could not read this photo clearly — please type it in.');
          return;
        }
        showConfirm(extractCandidates(text));
      })
      .catch(function () {
        showManualFallback('We could not read this photo clearly — please type it in.');
      });
  }

  /* Extract CANDIDATES only. We never decide whether the record is good. */
  function extractCandidates(text) {
    var t = String(text || '');

    var date = '';
    var dm = t.match(/\b(20\d{2})[-\/.](\d{1,2})[-\/.](\d{1,2})\b/);
    if (dm) { date = dm[1] + '-' + pad2(dm[2]) + '-' + pad2(dm[3]); }

    var unit = '';
    var um = t.match(/(?:sterilizer|unit|machine)\s*#?\s*(\d{1,3})/i);
    if (um) { unit = um[1]; }

    /* Result word: surfaced for confirmation only, never interpreted. */
    var result = '';
    var lower = t.toLowerCase();
    for (var i = 0; i < RESULT_TOKENS.length; i++) {
      var re = new RegExp('\\b' + RESULT_TOKENS[i] + '\\b', 'i');
      if (re.test(lower)) { result = RESULT_TOKENS[i]; break; }
    }

    return { date: date, unit: unit, result: result };
  }

  function showConfirm(cand) {
    if (!confirmForm) { return; }
    confirmForm.hidden = false;
    var f = { date: confirmForm.elements['date'], unit: confirmForm.elements['unit'], result: confirmForm.elements['result'] };
    if (f.date) { f.date.value = cand.date || ''; }
    if (f.unit) { f.unit.value = cand.unit || ''; }
    if (f.result) { f.result.value = cand.result || ''; }
    setStatus('We read some text from your photo. Please check each field, fix anything that is wrong, then save.');
  }

  function showManualFallback(message) {
    if (!confirmForm) { return; }
    confirmForm.hidden = false;
    var f = { date: confirmForm.elements['date'], unit: confirmForm.elements['unit'], result: confirmForm.elements['result'] };
    if (f.date) { f.date.value = ''; }
    if (f.unit) { f.unit.value = ''; }
    if (f.result) { f.result.value = ''; }
    setStatus(message, true);
  }

  if (shotInput) {
    shotInput.addEventListener('change', function (e) {
      var files = e.target.files || [];
      if (files.length) { handleFile(files[0]); }
    });
  }

  /* Desktop drag & drop onto the same single control. */
  if (drop) {
    ['dragenter', 'dragover'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('is-drag'); });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('is-drag'); });
    });
    drop.addEventListener('drop', function (e) {
      var dt = e.dataTransfer;
      if (dt && dt.files && dt.files.length) { handleFile(dt.files[0]); }
    });
  }

  /* Desktop paste (screenshot straight onto the page). */
  document.addEventListener('paste', function (e) {
    var cb = e.clipboardData;
    if (!cb || !cb.files || !cb.files.length) { return; }
    for (var i = 0; i < cb.files.length; i++) {
      if (/^image\//.test(cb.files[i].type || '')) { handleFile(cb.files[i]); break; }
    }
  });

  /* Buttons inside every action card focus the one shared photo control. */
  $$('[data-sc-photo-trigger]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var shot = $('#sc-shot');
      if (shot) { shot.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      if (shotInput) { shotInput.click(); }
    });
  });

  /* Confirm form: only here does anything get written — and only by the user. */
  if (confirmForm) {
    confirmForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var d = confirmForm.elements['date'] ? confirmForm.elements['date'].value : '';
      var u = confirmForm.elements['unit'] ? confirmForm.elements['unit'].value : '';
      var r = confirmForm.elements['result'] ? confirmForm.elements['result'].value : '';
      saveConfirmed({ date: d, unit: u, result: r });
      confirmForm.hidden = true;
      setStatus('Saved to this page. It was not sent anywhere.');
    });

    var manual = $('#sc-manual');
    if (manual) {
      manual.addEventListener('click', function () {
        showManualFallback('No problem — type the fields in and save.');
      });
    }
  }

  function saveConfirmed(rec) {
    if (!savedList) { return; }
    var li = document.createElement('li');
    li.textContent = 'Saved (on this device only) — Date: ' + (rec.date || '—') +
      ' · Sterilizer: ' + (rec.unit || '—') +
      ' · Result: ' + (rec.result || '—');
    savedList.appendChild(li);
  }

  /* ------------------------------------------------------------------ print */

  function stateLabel(s) {
    if (s === STATE.INPLACE) { return '✅ already in place'; }
    if (s === STATE.VERIFY) { return '⚠️ needs verification'; }
    if (s === STATE.ACTION) { return '🔴 needs action'; }
    return '— not answered';
  }

  function buildPrintHtml() {
    var na = $('[data-sc-count="action"]');
    var nv = $('[data-sc-count="verify"]');
    var ni = $('[data-sc-count="inplace"]');
    var answered = 0;
    items.forEach(function (it) { if (readState(it)) { answered++; } });

    var rows = items.map(function (it) {
      var id = it.getAttribute('data-sc-item');
      var q = it.querySelector('.sc-item__q');
      var question = q ? q.textContent.replace(/\s+/g, ' ').trim() : id;
      var s = readState(it);
      var line = '<tr><td class="id">' + escapeHtml(id) + '</td><td>' + escapeHtml(question) +
        '</td><td class="st">' + escapeHtml(stateLabel(s)) + '</td></tr>';
      /* F1 carries the 🟡 "not checked word-for-word" label, honestly shown. */
      if (id === 'F1') {
        line += '<tr class="flag"><td></td><td colspan="2">' + escapeHtml(F1_LABEL) +
          ' — general IPAC practice, not quoted from the RCDSO text.</td></tr>';
      }
      return line;
    }).join('');

    return '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">' +
      '<meta name="viewport" content="width=device-width, initial-scale=1">' +
      '<title>Sterilization Compliance Self-Check — Dental (Ontario)</title>' +
      '<style>' +
      'body{font-family:-apple-system,Segoe UI,Roboto,Arial,"PingFang SC","Microsoft YaHei",sans-serif;' +
      'color:#111827;margin:24px;line-height:1.5}' +
      'h1{font-size:1.4rem;margin:0 0 4px}' +
      '.meta{color:#5B6472;font-size:.9rem;margin:0 0 12px}' +
      '.sum{border:1px solid #1E5EFF;border-radius:10px;padding:10px 12px;margin:12px 0}' +
      '.sum p{margin:2px 0}' +
      'table{border-collapse:collapse;width:100%;font-size:.9rem}' +
      'th,td{border:1px solid #D7DEE8;padding:6px 8px;text-align:left;vertical-align:top}' +
      'th{background:#F3F7FF}' +
      'td.id{white-space:nowrap;font-weight:700;color:#1E5EFF;width:3em}' +
      'td.st{white-space:nowrap;width:12em}' +
      'tr.flag td{color:#B54708;font-size:.82rem}' +
      '@media print{body{margin:12mm}}' +
      '</style></head><body>' +
      '<h1>Sterilization Compliance Self-Check — Dental (Ontario)</h1>' +
      '<p class="meta">Printed: ' + escapeHtml(todayISO()) +
      ' · Completed: ' + answered + ' / ' + TOTAL + ' items</p>' +
      '<div class="sum">' +
      '<p>🔴 ' + (na ? na.textContent : '0') + ' items need action</p>' +
      '<p>⚠️ ' + (nv ? nv.textContent : '0') + ' items need verification</p>' +
      '<p>✅ ' + (ni ? ni.textContent : '0') + ' items already in place</p>' +
      '<p class="meta">Based on: RCDSO IPAC Standard of Practice (v3) · ' +
      'RCDSO IPAC Self Audit Review Form (v2)</p>' +
      '</div>' +
      '<p><strong>This is a self-check list.</strong> It is not a compliance determination ' +
      'and does not replace an inspection by your regulator.</p>' +
      '<table><thead><tr><th>#</th><th>Item</th><th>Your answer</th></tr></thead>' +
      '<tbody>' + rows + '</tbody></table>' +
      '</body></html>';
  }

  var printBtn = $('#sc-print');
  if (printBtn) {
    printBtn.addEventListener('click', function () {
      var win = window.open('', '_blank');
      if (!win) { setStatus('Please allow pop-ups for this page to print.', true); return; }
      win.document.open();
      win.document.write(buildPrintHtml());
      win.document.close();
      win.focus();
      setTimeout(function () { try { win.print(); } catch (e) { /* user can print manually */ } }, 250);
    });
  }

  /* ------------------------------------------------------------------- init */

  recompute();
})();
